import { useState, type FormEvent, type ReactNode } from 'react';
import {
  useConnectionsQuery, useCreateWebhookMutation, useCreateZapierKeyMutation, useDestinationsQuery,
  useRetryDeliveryMutation, useRevokeDestinationMutation, useRevokeZapierKeyMutation,
  useRotateDestinationSecretMutation, useTestDestinationMutation, useUpdateDestinationMutation,
  useCompanionConnectionsQuery, useRevokeCompanionMutation,
  type DeliveryRow, type DestinationView, type DestinationsResponse,
} from './api.js';
import { Alert, Button, Card, EmptyState, Spinner, formatWhen } from './ui.js';

/**
 * Settings → Integrations: where approved contacts go.
 *
 * Keap is the native connection (Settings → Connections). Zapier and webhooks receive the
 * same approved contact as an event, after a person approves it — never before, and never
 * contacts approved before the destination existed. Each destination says what it does
 * and does not do, because they are not alike: a delivered webhook proves the receiver
 * accepted it, not that a CRM changed.
 */

const DELIVERY_TEXT: Record<DeliveryRow['status'], { label: string; cls: string }> = {
  pending: { label: 'Sending', cls: 'bg-sky-50 text-sky-800 ring-sky-600/20' },
  delivered: { label: 'Delivered', cls: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20' },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-800 ring-red-600/20' },
  gone: { label: 'Zap turned off', cls: 'bg-slate-100 text-slate-700 ring-slate-500/20' },
};

const ERROR_TEXT: Record<string, string> = {
  unsafe_url: 'The address is not a public HTTPS address.',
  redirect_not_followed: 'The address redirected. Use the final URL.',
  timeout: 'The receiver did not answer within 10 seconds.',
  network_error: 'The receiver could not be reached.',
  destination_not_active: 'The destination was turned off before this was sent.',
  subscription_gone: 'Zapier said this Zap no longer exists.',
};
const errorText = (code: string | null) => (code ? ERROR_TEXT[code] ?? (code.startsWith('http_') ? `The receiver answered ${code.slice(5)}.` : code) : null);

function Pill({ children, cls }: { children: ReactNode; cls: string }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>{children}</span>;
}

/** A secret shown exactly once, with a copy button. */
function OneTimeSecret({ label, value, onDone }: { label: string; value: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-3" role="alert">
      <p className="text-sm font-semibold text-amber-900">{label}</p>
      <p className="text-xs text-amber-900">Copy it now. ContactPilot stores only an encrypted or hashed form and cannot show it again.</p>
      <code className="block break-all rounded-lg bg-white px-2 py-1.5 text-sm text-slate-900" data-one-time-secret="">{value}</code>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => { void navigator.clipboard?.writeText(value).then(() => setCopied(true)); }}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button variant="secondary" onClick={onDone}>I have saved it</Button>
      </div>
    </div>
  );
}

const field = 'mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-cp-blue-600 focus:outline-none focus:ring-2 focus:ring-cp-blue-600/25';

export function Integrations({ canEdit, onOpenConnections }: { canEdit: boolean; onOpenConnections: () => void }) {
  const { data, isLoading, isError } = useDestinationsQuery();
  const conn = useConnectionsQuery();
  const keap = conn.data?.connectors.find((c) => c.kind === 'keap');

  if (isLoading) return <Spinner />;
  if (isError || !data) return <Alert tone="error">Integrations could not be loaded. Try again.</Alert>;

  const webhooks = data.destinations.filter((d) => d.kind === 'webhook');
  const zaps = data.destinations.filter((d) => d.kind === 'zapier');
  const names = new Map(data.destinations.map((d) => [d.id, d.name]));

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl font-bold text-slate-900">Integrations</h2>
        <p className="mt-1 text-sm text-slate-600">
          Where approved contacts go. Nothing is sent before you approve it, and contacts approved before you add a destination are never sent to it.
        </p>
      </header>

      {!canEdit ? <Alert tone="info">Only a workspace owner or admin can change integrations.</Alert> : null}

      {/* ── Keap ───────────────────────────────────────────────────────── */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-slate-900">Keap</h3>
          <Pill cls="bg-emerald-50 text-emerald-800 ring-emerald-600/20">Available · native</Pill>
          {keap ? <Pill cls="bg-slate-100 text-slate-700 ring-slate-500/20">{keap.status === 'CONNECTED' ? 'Connected' : 'Not connected'}</Pill> : null}
        </div>
        <p className="mt-2 text-sm text-slate-600">
          Creates new contacts, applies your tags, sets Contact Type, and adds email notes — and shows a contact as sent only after Keap confirms it.
        </p>
        <button type="button" onClick={onOpenConnections} className="mt-2 inline-flex min-h-11 items-center text-sm font-semibold text-cp-blue-600 underline">
          Manage the Keap connection
        </button>
      </Card>

      <CompanionCard canEdit={canEdit} />
      <ZapierCard data={data} zaps={zaps} canEdit={canEdit} />
      <WebhooksCard webhooks={webhooks} canEdit={canEdit} sample={data.sample} />

      <Deliveries rows={data.deliveries} names={names} canEdit={canEdit} />
    </div>
  );
}

function ZapierCard({ data, zaps, canEdit }: { data: DestinationsResponse; zaps: DestinationView[]; canEdit: boolean }) {
  const [createKey, creating] = useCreateZapierKeyMutation();
  const [revokeKey] = useRevokeZapierKeyMutation();
  const [secret, setSecret] = useState<string | null>(null);
  const keys = data.zapierKeys.filter((k) => !k.revokedAt);
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-slate-900">Zapier</h3>
        <Pill cls="bg-amber-50 text-amber-900 ring-amber-600/25">Beta · private</Pill>
      </div>
      <p className="mt-2 text-sm text-slate-600">
        The ContactPilot Zapier app offers a <strong>New Approved Contact</strong> trigger. Your Zap decides what happens next in your CRM —
        create or update a contact, choose a pipeline and stage, add tags. What is possible depends on your CRM&rsquo;s own Zapier actions,
        and some need a paid Zapier plan. You use your own Zapier account.
      </p>
      <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-slate-700">
        <li>Create a connection key below and copy it.</li>
        <li>In Zapier, choose the ContactPilot app (by invitation while it is private) and paste the key.</li>
        <li>Pick <em>New Approved Contact</em> as the trigger and add your CRM&rsquo;s action.</li>
      </ol>
      <p className="mt-2 text-xs text-slate-500">A delivered event means Zapier received it. Whether your CRM changed is shown in your Zap&rsquo;s history.</p>

      {secret ? <div className="mt-3"><OneTimeSecret label="Your Zapier connection key" value={secret} onDone={() => setSecret(null)} /></div> : null}

      <div className="mt-4 space-y-2">
        <h4 className="text-sm font-semibold text-slate-800">Connection keys</h4>
        {keys.length === 0 ? <p className="text-sm text-slate-600">No connection key yet.</p> : (
          <ul className="divide-y divide-slate-100 rounded-xl border border-cp-border">
            {keys.map((k) => (
              <li key={k.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span><span className="font-medium text-slate-800">{k.name}</span> <code className="text-xs text-slate-500">{k.prefix}…</code></span>
                <span className="text-xs text-slate-500">{k.lastUsedAt ? `Last used ${formatWhen(k.lastUsedAt)}` : 'Not used yet'}</span>
                {canEdit ? (
                  <Button variant="secondary" onClick={() => { if (window.confirm('Revoke this key? Every Zap using it stops receiving contacts.')) void revokeKey(k.id); }}>
                    Revoke
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canEdit ? (
          <Button disabled={creating.isLoading} onClick={async () => {
            const r = await createKey({ name: 'Zapier' });
            if ('data' in r && r.data) setSecret(r.data.secret);
          }}>
            Create connection key
          </Button>
        ) : null}
        <p className="text-sm text-slate-600">{zaps.length === 0 ? 'No Zap is subscribed.' : `${zaps.length} Zap${zaps.length === 1 ? '' : 's'} subscribed.`}</p>
      </div>
    </Card>
  );
}

function WebhooksCard({ webhooks, canEdit, sample }: { webhooks: DestinationView[]; canEdit: boolean; sample: Record<string, unknown> }) {
  const [create, creating] = useCreateWebhookMutation();
  const [update] = useUpdateDestinationMutation();
  const [revoke] = useRevokeDestinationMutation();
  const [rotate] = useRotateDestinationSecretMutation();
  const [test] = useTestDestinationMutation();
  const [form, setForm] = useState({ name: '', url: '', leadSource: '', pipeline: '', stage: '', tags: '' });
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const r = await create({
      name: form.name, url: form.url,
      organization: {
        leadSource: form.leadSource || null, pipeline: form.pipeline || null, stage: form.stage || null,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      },
    });
    if ('error' in r) {
      const body = (r.error as { data?: { message?: string } }).data;
      setError(body?.message ?? 'That webhook could not be added. Use a public HTTPS URL.');
      return;
    }
    setSecret(r.data.signingSecret);
    setForm({ name: '', url: '', leadSource: '', pipeline: '', stage: '', tags: '' });
  };

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-slate-900">Webhooks (Make, n8n and others)</h3>
        <Pill cls="bg-amber-50 text-amber-900 ring-amber-600/25">Beta</Pill>
      </div>
      <p className="mt-2 text-sm text-slate-600">
        Each approved contact is POSTed as JSON to your HTTPS address, signed with a secret only you and ContactPilot know.
        Make, n8n or another tool then does what you design. Their costs and hosting are yours.
      </p>

      {secret ? <div className="mt-3"><OneTimeSecret label="Your webhook signing secret" value={secret} onDone={() => setSecret(null)} /></div> : null}
      {notice ? <div className="mt-3"><Alert tone="info">{notice}</Alert></div> : null}

      {webhooks.length === 0 ? <div className="mt-4"><EmptyState title="No webhook yet">Add one below to start sending approved contacts.</EmptyState></div> : (
        <ul className="mt-4 space-y-3">
          {webhooks.map((w) => (
            <li key={w.id} className="rounded-xl border border-cp-border p-3" data-webhook="">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-900">{w.name}</span>
                <Pill cls={w.status === 'active' ? 'bg-emerald-50 text-emerald-800 ring-emerald-600/20' : 'bg-slate-100 text-slate-700 ring-slate-500/20'}>
                  {w.status === 'active' ? 'On' : 'Off'}
                </Pill>
                <code className="min-w-0 break-all text-xs text-slate-500">{w.url}</code>
              </div>
              <p className="mt-1 text-xs text-slate-600">
                {[w.organization.leadSource && `Lead source: ${w.organization.leadSource}`, w.organization.pipeline && `Pipeline: ${w.organization.pipeline}`,
                  w.organization.stage && `Stage: ${w.organization.stage}`, w.organization.tags?.length ? `Extra tags: ${w.organization.tags.join(', ')}` : null]
                  .filter(Boolean).join(' · ') || 'Sends the contact as approved, with its source as the lead source.'}
              </p>
              {w.lastErrorCode ? <p className="mt-1 text-xs text-red-700">Last problem: {errorText(w.lastErrorCode)}</p> : null}
              {canEdit ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={async () => {
                    const r = await test(w.id);
                    setNotice('data' in r && r.data?.ok ? `Test sent to ${w.name}: delivered.` : `Test to ${w.name} was not delivered. ${errorText('data' in r ? r.data?.errorCode ?? (r.data?.statusCode ? `http_${r.data.statusCode}` : null) : null) ?? ''}`);
                  }}>Send test</Button>
                  <Button variant="secondary" onClick={() => void update({ id: w.id, status: w.status === 'active' ? 'disabled' : 'active' })}>
                    {w.status === 'active' ? 'Turn off' : 'Turn on'}
                  </Button>
                  <Button variant="secondary" onClick={async () => {
                    if (!window.confirm('Create a new signing secret? The old one stops working immediately.')) return;
                    const r = await rotate(w.id);
                    if ('data' in r && r.data) setSecret(r.data.signingSecret);
                  }}>New secret</Button>
                  <Button variant="secondary" onClick={() => { if (window.confirm(`Remove ${w.name}? It stops receiving contacts. Its delivery history stays.`)) void revoke(w.id); }}>
                    Remove
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <form onSubmit={submit} className="mt-4 space-y-3 rounded-xl bg-slate-50 p-3" aria-label="Add a webhook">
          <h4 className="text-sm font-semibold text-slate-800">Add a webhook</h4>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm"><span className="text-slate-700">Name</span>
              <input required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} placeholder="Make scenario" /></label>
            <label className="block text-sm"><span className="text-slate-700">HTTPS address</span>
              <input required type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={field} placeholder="https://hook.example.com/…" /></label>
          </div>
          <details className="text-sm">
            <summary className="flex min-h-11 cursor-pointer items-center font-medium text-slate-700">Organisation fields (optional)</summary>
            <p className="text-xs text-slate-500">Sent with every contact so your automation can file it. Your CRM decides what they mean; ContactPilot never invents a pipeline.</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <label className="block"><span className="text-slate-700">Lead source</span>
                <input maxLength={120} value={form.leadSource} onChange={(e) => setForm({ ...form, leadSource: e.target.value })} className={field} placeholder="Defaults to Business Card, Email or CSV Import" /></label>
              <label className="block"><span className="text-slate-700">Extra tags (comma-separated)</span>
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={field} placeholder="Networking" /></label>
              <label className="block"><span className="text-slate-700">Pipeline</span>
                <input maxLength={120} value={form.pipeline} onChange={(e) => setForm({ ...form, pipeline: e.target.value })} className={field} /></label>
              <label className="block"><span className="text-slate-700">Stage</span>
                <input maxLength={120} value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className={field} /></label>
            </div>
          </details>
          {error ? <Alert tone="error">{error}</Alert> : null}
          <Button type="submit" disabled={creating.isLoading}>Add webhook</Button>
        </form>
      ) : null}

      <details className="mt-4 text-sm">
        <summary className="flex min-h-11 cursor-pointer items-center font-medium text-slate-700">What is sent, and how to verify it</summary>
        <p className="text-slate-600">
          Header <code>ContactPilot-Signature: t=&lt;seconds&gt;,v1=&lt;hex&gt;</code> is HMAC-SHA256 of <code>&lt;t&gt;.&lt;body&gt;</code> with your secret.
          Reject anything older than five minutes. Deduplicate on <code>event_id</code>: delivery is at least once.
        </p>
        <pre className="mt-2 max-h-80 overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-100">{JSON.stringify(sample, null, 2)}</pre>
      </details>
    </Card>
  );
}

function Deliveries({ rows, names, canEdit }: { rows: DeliveryRow[]; names: Map<string, string>; canEdit: boolean }) {
  const [retry] = useRetryDeliveryMutation();
  return (
    <Card className="p-5">
      <h3 className="text-base font-semibold text-slate-900">Recent deliveries</h3>
      {rows.length === 0 ? <p className="mt-2 text-sm text-slate-600">Nothing sent yet. Approved contacts appear here once a destination is on.</p> : (
        <ul className="mt-3 divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <Pill cls={DELIVERY_TEXT[r.status].cls}>{DELIVERY_TEXT[r.status].label}</Pill>
              <span className="min-w-0 flex-1 truncate text-slate-800">{r.contactName ?? 'Contact'} → {names.get(r.destinationId) ?? 'Removed destination'}</span>
              <span className="text-xs text-slate-500">{formatWhen(r.deliveredAt ?? r.createdAt)}{r.attemptCount > 1 ? ` · ${r.attemptCount} attempts` : ''}</span>
              {r.status === 'failed' ? <span className="w-full text-xs text-red-700">{errorText(r.lastErrorCode)}</span> : null}
              {r.status === 'failed' && canEdit ? <Button variant="secondary" onClick={() => void retry(r.id)}>Retry</Button> : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * The Browser Companion's connected browsers. It is a capture source, not a destination:
 * what it saves waits in Review like everything else.
 */
function CompanionCard({ canEdit }: { canEdit: boolean }) {
  const { data } = useCompanionConnectionsQuery();
  const [revoke] = useRevokeCompanionMutation();
  const list = data?.connections ?? [];
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-slate-900">Browser Companion</h3>
        <Pill cls="bg-amber-50 text-amber-900 ring-amber-600/25">Private testing</Pill>
      </div>
      <p className="mt-2 text-sm text-slate-600">
        A Chrome extension that captures one person from the web page you are viewing, when you click it. You check the details,
        and the contact waits in Review like any other capture. It does not work on LinkedIn or other restricted sites.
      </p>
      {data?.limits ? <p className="mt-1 text-xs text-slate-500">Up to {data.limits.dailyCaptures} captures per person per day.</p> : null}
      {list.length === 0 ? <p className="mt-3 text-sm text-slate-600">No browser is connected.</p> : (
        <ul className="mt-3 divide-y divide-slate-100 rounded-xl border border-cp-border">
          {list.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-companion-connection="">
              <span><span className="font-medium text-slate-800">{c.name}</span> <span className="text-xs text-slate-500">{c.person ?? ''}</span></span>
              <span className="text-xs text-slate-500">{c.lastUsedAt ? `Last used ${formatWhen(c.lastUsedAt)}` : 'Not used yet'}</span>
              {canEdit || c.mine ? (
                <Button variant="secondary" onClick={() => { if (window.confirm('Disconnect this browser? It stops capturing until it is connected again.')) void revoke(c.id); }}>
                  Disconnect
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
